import {
  Loading,
  Title,
  useGetList,
  useLogout,
  useNotify,
  usePermissions,
  useRedirect,
  useTheme,
  useTranslate
} from 'react-admin'
import {
  Card,
  CardContent,
  Grid,
  Button,
  Typography,
  useMediaQuery
} from '@mui/material'
import log from 'loglevel'
import React from 'react'
import { type Partner, type Product } from '@mzc-watchover/adminapp-appsync'
import { head, includes, isEmpty, isNil, map, split } from 'lodash/fp'
import { useParams } from 'react-router-dom'

import { UserEdit } from 'components/User'
import {
  CognitoDownloadDialog,
  DownloadDialog,
  SummaryDownloadDialog
} from 'components/Download'
import { CognitoUploadDialog, UploadDialog } from 'components/Upload'
import { PartnerSelectDialog } from 'components/PartnerSelect'
import { Announce } from 'components/Announce'
import { Cognito } from 'components/Cognito'
import { ContactEdit } from 'components/Contact'
import { useGetSummaryItems } from 'components/DataProvider/hooks'

log.setLevel(log.levels.DEBUG)

export const Dashboard: React.FC<{
  setPartner?: (partner: Partial<Partner>) => void
  getPartner?: () => Partial<Partner> | undefined
}> = ({ setPartner, getPartner }) => {
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('md'))
  const isLarge = useMediaQuery((theme: any) => theme.breakpoints.up('lg'))
  const [edit, setEdit] = React.useState(false)
  const [download, setDownload] = React.useState(false)
  const [upload, setUpload] = React.useState(false)
  const [adminDownload, setAdminDownload] = React.useState(false)
  const [adminUpload, setAdminUpload] = React.useState(false)
  const [partnerSelect, setPartnerSelect] = React.useState(false)
  const [editContact, setEditContact] = React.useState(false)
  const [getSummary, setGetSummary] = React.useState(false)
  // const [partners, setPartners] = React.useState<
  //   Array<{ id: string; name: string }> | undefined
  // >(undefined)
  const [products, setProducts] = React.useState<
    Array<Partial<Product>> | undefined
  >(undefined)
  const [partnerName, setPartnerName] = React.useState<string | undefined>(
    undefined
  )
  // const [page, setPage] = React.useState<number>(1)
  const { permissions, isLoading: isLoadingPermissions } = usePermissions()
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_theme, setTheme] = useTheme()
  const notify = useNotify()
  const logout = useLogout()
  const translate = useTranslate()
  const { cognito } = useParams()
  const redirect = useRedirect()
  const { data: summaryItems, isLoading: isLoadingSummaryItems } =
    useGetSummaryItems(
      !isNil(getPartner) ? (getPartner() ?? { id: undefined }).id : undefined
    )
  const onEdit = (): void => {
    setEdit(true)
  }
  const onDownload = (): void => {
    setEdit(false)
    setDownload(true)
  }
  const onUpload = (): void => {
    setEdit(false)
    setUpload(true)
  }
  const onAdminDownload = (): void => {
    setAdminDownload(true)
  }
  const onAdminUpload = (): void => {
    setAdminUpload(true)
  }
  const onEditContact = (): void => {
    setEditContact(true)
  }
  const onGetSummary = (): void => {
    setGetSummary(true)
  }
  const { data, isLoading, total } = useGetList<
    { id: string } & Partial<Partner>
  >('partners', {
    pagination: { page: 1, perPage: 10 }
  })
  React.useEffect(() => {
    if (isLoading || isLoadingPermissions) return
    setTheme(
      !isNil(cognito) && includes('admin')(permissions) ? 'dark' : 'light'
    )
    if (!isNil(cognito) && includes('admin')(permissions)) {
      setPartnerName('管理者')
    } else if (!isNil(data) && !isEmpty(data)) {
      if (total === 1) {
        if (!isNil(data[0])) {
          const { name, products } = data[0]
          setPartnerName(`${name as string}さま`)
          setProducts(products)
          if (!isNil(setPartner)) setPartner(data[0])
        }
      } else {
        // setPartners(
        //   map((item: Partial<Partner>) => {
        //     const { id, name, products } = item
        //     return { id: id as string, name: name as string, products }
        //   })(data)
        // )
        setPartnerSelect(true)
      }
    }
  }, [isLoading, cognito])
  if (
    isLoading ||
    isLoadingPermissions ||
    isLoadingSummaryItems ||
    isNil(total)
  )
    return <Loading />
  if (isNil(data) || isEmpty(data)) {
    if (isNil(cognito) && !includes('admin')(permissions)) {
      notify('resources.dashboard.notification.cannotUse', { type: 'error' })
      logout()
        .then(() => {})
        .catch(log.error)
      return <></>
    } else {
      if (isNil(cognito)) {
        redirect('/admin')
        return <></>
      }
    }
  }
  const buttonWidth = isDesktop ? (isLarge ? '220px' : '220px') : '450px'
  const buttonHeight = isDesktop ? '60px' : '40px'
  const buttonSx = isDesktop
    ? {
        marginTop: '1em',
        marginLeft: '2em',
        marginRight: '2em',
        marginBottom: '1em'
      }
    : {
        marginTop: '0.5em',
        marginLeft: '2em',
        marginRight: '2em',
        marginBottom: '0.5em'
      }
  if (
    (isNil(cognito) || !includes('admin')(permissions)) &&
    total > 1 &&
    !partnerSelect &&
    isNil(products)
  ) {
    setPartnerSelect(true)
  }
  const partner = !isNil(getPartner) ? getPartner() : undefined
  log.info({ summaryItems })
  return (
    <Grid container justifyContent="center">
      <Title title={`${!isNil(partnerName) ? partnerName : ''}`} />
      <Announce />
      <Card
        raised
        sx={{
          width: '1280px',
          marginTop: '1em',
          marginBottom: '1em',
          backgroundColor: 'background'
        }}>
        <CardContent>
          {!isNil(cognito) && partnerName === '管理者' ? (
            <Grid container justifyContent="center">
              <Grid
                item
                sx={{
                  marginTop: '1em',
                  marginLeft: '3em',
                  marginRight: '3em',
                  marginBottom: '1em'
                }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  sx={{
                    width: buttonWidth,
                    height: buttonHeight,
                    borderRadius: '10px'
                  }}
                  onClick={onAdminDownload}>
                  <Typography variant="h6">
                    {translate('resources.dashboard.admin.download')}
                  </Typography>
                </Button>
              </Grid>
              <Grid item sx={buttonSx}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  sx={{
                    width: buttonWidth,
                    height: buttonHeight,
                    borderRadius: '10px'
                  }}
                  onClick={onAdminUpload}>
                  <Typography variant="h6">
                    {translate('resources.dashboard.admin.upload')}
                  </Typography>
                </Button>
              </Grid>
            </Grid>
          ) : (
            <Grid container justifyContent="center">
              <Grid item sx={buttonSx}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  sx={{
                    width: buttonWidth,
                    height: buttonHeight,
                    borderRadius: '10px'
                  }}
                  onClick={onEdit}>
                  <Typography variant="h6">
                    {translate('resources.dashboard.menu.edit')}
                  </Typography>
                </Button>
              </Grid>
              <Grid item sx={buttonSx}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  sx={{
                    width: buttonWidth,
                    height: buttonHeight,
                    borderRadius: '10px'
                  }}
                  onClick={onDownload}>
                  <Typography variant="h6">
                    {translate('resources.dashboard.menu.download')}
                  </Typography>
                </Button>
              </Grid>
              <Grid item sx={buttonSx}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  sx={{
                    width: buttonWidth,
                    height: buttonHeight,
                    borderRadius: '10px'
                  }}
                  onClick={onUpload}>
                  <Typography variant="h6">
                    {/* @ts-expect-error ts(2339) */}
                    {map.convert({ cap: false })(
                      (item: string, index: number) => (
                        <span key={index}>
                          {item}
                          <br />
                        </span>
                      )
                    )(
                      split('\n')(translate('resources.dashboard.menu.upload'))
                    )}
                  </Typography>
                </Button>
              </Grid>
              {includes('editContact')(permissions) && (
                <Grid item sx={buttonSx}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    sx={{
                      width: buttonWidth,
                      height: buttonHeight,
                      borderRadius: '10px'
                    }}
                    onClick={onEditContact}>
                    <Typography variant="h6">
                      {/* @ts-expect-error ts(2339) */}
                      {map.convert({ cap: false })(
                        (item: string, index: number) => (
                          <span key={index}>
                            {item}
                            <br />
                          </span>
                        )
                      )(
                        split('\n')(
                          translate('resources.dashboard.menu.editContact')
                        )
                      )}
                    </Typography>
                  </Button>
                </Grid>
              )}
              {!isEmpty(summaryItems) && (
                <Grid item sx={buttonSx}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    sx={{
                      width: buttonWidth,
                      height: buttonHeight,
                      borderRadius: '10px'
                    }}
                    onClick={onGetSummary}>
                    <Typography variant="h6">
                      {/* @ts-expect-error ts(2339) */}
                      {map.convert({ cap: false })(
                        (item: string, index: number) => (
                          <span key={index}>
                            {item}
                            <br />
                          </span>
                        )
                      )(
                        split('\n')(
                          translate('resources.dashboard.menu.summary')
                        )
                      )}
                    </Typography>
                  </Button>
                </Grid>
              )}
            </Grid>
          )}
        </CardContent>
      </Card>
      {!isNil(cognito) && partnerName === '管理者' && cognito !== 'admin' && (
        <Cognito id={cognito} />
      )}
      {edit && (
        <UserEdit partner={!isNil(getPartner) ? getPartner() : undefined} />
      )}
      {editContact && (
        <ContactEdit partner={!isNil(getPartner) ? getPartner() : undefined} />
      )}
      {getSummary && !isNil(partner) && (
        <SummaryDownloadDialog
          open={getSummary}
          setOpen={setGetSummary}
          partner={partner.id as string}
          item={head(summaryItems) as string}
        />
      )}
      <DownloadDialog
        open={download}
        setOpen={setDownload}
        partner={!isNil(getPartner) ? getPartner() : undefined}
      />
      <UploadDialog
        open={upload}
        setOpen={setUpload}
        partner={!isNil(getPartner) ? getPartner() : undefined}
      />
      <CognitoDownloadDialog open={adminDownload} setOpen={setAdminDownload} />
      <CognitoUploadDialog open={adminUpload} setOpen={setAdminUpload} />
      <PartnerSelectDialog
        open={partnerSelect}
        setOpen={setPartnerSelect}
        // setPartners={setPartners}
        setProducts={setProducts}
        setPartnerName={setPartnerName}
        setPartner={setPartner}
      />
    </Grid>
  )
}
